/**
 * CommonFieldsForm Component
 * Shared form fields for content item creation/editing
 */

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { FluentCard } from '@/components/layout/page-header'
import { FolderSelector } from './FolderSelector'
import { TagsInput } from './TagsInput'
import { cn } from '@/lib/utils'

// ============================================
// Types
// ============================================

export interface CommonFieldsFormData {
  name: string
  description: string
  folderId: string | null
  tags: string[]
  isActive?: boolean
}

interface CommonFieldsFormProps {
  value: CommonFieldsFormData
  onChange: (data: CommonFieldsFormData) => void
  errors?: Partial<Record<keyof CommonFieldsFormData, string>>
  disabled?: boolean
  showActiveToggle?: boolean
  className?: string
}

// ============================================
// CommonFieldsForm Component
// ============================================

export function CommonFieldsForm({
  value,
  onChange,
  errors,
  disabled = false,
  showActiveToggle = false,
  className,
}: CommonFieldsFormProps) {
  const updateField = <K extends keyof CommonFieldsFormData>(
    field: K,
    fieldValue: CommonFieldsFormData[K]
  ) => {
    onChange({ ...value, [field]: fieldValue })
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* Basic Information */}
      <FluentCard>
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
          Basic Information
        </h2>

        <div className="space-y-4">
          {/* Name Field */}
          <div className="space-y-2">
            <Label htmlFor="name">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              type="text"
              placeholder="Enter content name"
              value={value.name}
              onChange={(e) => updateField('name', e.target.value)}
              disabled={disabled}
              className={cn('rounded-xl', errors?.name && 'border-red-500 focus:ring-red-500')}
              aria-invalid={!!errors?.name}
              aria-describedby={errors?.name ? 'name-error' : undefined}
            />
            {errors?.name && (
              <p id="name-error" className="text-sm text-red-500">
                {errors.name}
              </p>
            )}
          </div>

          {/* Description Field */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Describe what this content is used for..."
              value={value.description}
              onChange={(e) => updateField('description', e.target.value)}
              disabled={disabled}
              rows={3}
              className={cn(
                'rounded-xl resize-none',
                errors?.description && 'border-red-500 focus:ring-red-500'
              )}
              aria-invalid={!!errors?.description}
              aria-describedby={errors?.description ? 'description-error' : undefined}
            />
            {errors?.description && (
              <p id="description-error" className="text-sm text-red-500">
                {errors.description}
              </p>
            )}
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              A brief description helps others understand when to use this content
            </p>
          </div>
        </div>
      </FluentCard>

      {/* Location Card */}
      <FluentCard>
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Location</h2>

        <div className="space-y-2">
          <Label>Folder</Label>
          <FolderSelector
            value={value.folderId}
            onChange={(folderId) => updateField('folderId', folderId)}
            disabled={disabled}
            placeholder="Select folder (optional)"
          />
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Organize content by placing it in a folder
          </p>
        </div>
      </FluentCard>

      {/* Tags Card */}
      <FluentCard>
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Tags</h2>

        <div className="space-y-2">
          <TagsInput
            value={value.tags}
            onChange={(tags) => updateField('tags', tags)}
            disabled={disabled}
            placeholder="Add tags to help find this content..."
            maxTags={10}
          />
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            Tags help organize and find content quickly. Press Enter or comma to add a tag.
          </p>
        </div>
      </FluentCard>

      {/* Active Toggle (for edit mode) */}
      {showActiveToggle && (
        <FluentCard>
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="is-active" className="text-base font-medium">
                Active Status
              </Label>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                {value.isActive
                  ? 'This content is available for use'
                  : 'This content is hidden from users'}
              </p>
            </div>
            <Switch
              id="is-active"
              checked={value.isActive ?? true}
              onCheckedChange={(checked) => updateField('isActive', checked)}
              disabled={disabled}
            />
          </div>
        </FluentCard>
      )}
    </div>
  )
}

export default CommonFieldsForm
